import axios from 'axios';
import { paramSerialize } from '../utils/stringUtils';

class GenericService {
    constructor(baseUrl, axiosInstance = axios) {
        this.baseUrl = baseUrl;
        this.axios = axiosInstance;
    }

    encodePathSegment(value) {
        return encodeURIComponent(String(value ?? ''));
    }

    async getAllList(queryParams = {}, condition = {}) {
        try {
            const rules = condition?.rules
            const serializedRules = paramSerialize(rules, 'rules');

            const serializedParams = Object.entries(queryParams)
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join('&');


            const queryString = `${serializedParams}&${serializedRules}`;
            const response = await this.axios.get(`${this.baseUrl}/all?${queryString}`);

            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Get list with pagination, filters, and sorting
    async getList(queryParams = {}, condition = {}, classicFilters = {}) {
        try {
            const response = await this.axios.get(`${this.baseUrl}?${this.buildQueryString(queryParams, condition, classicFilters)}`);

            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Total row count for the same filters as getList (endpoint: {baseUrl}/count)
    async getCount(queryParams = {}, condition = {}, classicFilters = {}) {
        try {
            const response = await this.axios.get(`${this.baseUrl}/count?${this.buildQueryString(queryParams, condition, classicFilters)}`);

            return response.data.total;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    buildQueryString(queryParams = {}, condition = {}, classicFilters = {}) {
        const rules = condition?.rules;

        let serializedRules = '';
        if (rules) {
            serializedRules = `&${paramSerialize(rules, 'rules')}`;
        }

        let serializedClassicFilters = '';
        if (Object.keys(classicFilters).length > 0) {
            // Remove options property from filters before serialization
            const cleanedFilters = Object.keys(classicFilters).reduce((acc, key) => {
                const { options, ...filterWithoutOptions } = classicFilters[key];
                acc[key] = filterWithoutOptions;
                return acc;
            }, {});

            serializedClassicFilters = `&${paramSerialize(cleanedFilters, 'classic_filters')}`;
        }

        const serializedParams = Object.entries(queryParams)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');

        return serializedParams + serializedRules + serializedClassicFilters;
    }

    // Get single item
    async getOne(id) {
        try {
            const encodedId = this.encodePathSegment(id);
            const response = await this.axios.get(`${this.baseUrl}/${encodedId}`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Create new item
    async create(data) {
        try {
            const response = await this.axios.post(this.baseUrl, data);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Update item
    async update(id, data) {
        try {
            const encodedId = this.encodePathSegment(id);
            const response = await this.axios.put(`${this.baseUrl}/${encodedId}`, data);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Delete item
    async delete(id) {
        try {
            const encodedId = this.encodePathSegment(id);
            const response = await this.axios.delete(`${this.baseUrl}/${encodedId}`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Download data
    async download(data, condition = {}, classicFilters = {}) {
        try {
            // Remove options property from filters before sending
            let cleanedClassicFilters = classicFilters;
            if (Object.keys(classicFilters).length > 0) {
                cleanedClassicFilters = Object.keys(classicFilters).reduce((acc, key) => {
                    const { options, ...filterWithoutOptions } = classicFilters[key];
                    acc[key] = filterWithoutOptions;
                    return acc;
                }, {});
            }
            
            const payload = {
                ...data,
                ...(condition?.rules && { rules: condition.rules }),
                ...(Object.keys(cleanedClassicFilters).length > 0 && { classic_filters: cleanedClassicFilters })
            };
            
            const response = await this.axios.post(`${this.baseUrl}/download`, payload);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Download for heavy reports: the server builds the file after replying, we poll until it is ready
    async downloadAsync(data, condition = {}, classicFilters = {}) {
        try {
            let cleanedClassicFilters = classicFilters;
            if (Object.keys(classicFilters).length > 0) {
                cleanedClassicFilters = Object.keys(classicFilters).reduce((acc, key) => {
                    const { options, ...filterWithoutOptions } = classicFilters[key];
                    acc[key] = filterWithoutOptions;
                    return acc;
                }, {});
            }

            const payload = {
                ...data,
                ...(condition?.rules && { rules: condition.rules }),
                ...(Object.keys(cleanedClassicFilters).length > 0 && { classic_filters: cleanedClassicFilters })
            };

            const start = await this.axios.post(`${this.baseUrl}/download_async`, payload);
            const exportId = start.data.export_id;

            const pollEveryMs = 2000;
            const giveUpAfterMs = 30 * 60 * 1000;
            const startedAt = Date.now();

            while (Date.now() - startedAt < giveUpAfterMs) {
                await new Promise(resolve => setTimeout(resolve, pollEveryMs));
                const status = (await this.axios.get(`/reports/export_status/${exportId}`)).data;
                if (status.status === 'done') {
                    return { success: true, url: status.url };
                }
                if (status.status === 'failed') {
                    throw { message: status.message || 'Export failed', status: 500 };
                }
            }
            throw { message: 'Export is taking too long. Try a smaller date range.', status: 504 };
        } catch (error) {
            throw error?.response ? this.handleError(error) : error;
        }
    }

    // Get table settings
    async getSettings() {
        try {
            const response = await this.axios.get(`${this.baseUrl}/table/settings`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Handle API errors
    handleError(error) {
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            const data = error.response.data ?? {};

            return {
                message: data.message || data.error || 'An error occurred',
                errors: data.errors || {},
                status: error.response.status
            };
        } else if (error.request) {
            // The request was made but no response was received
            return {
                message: 'No response from server',
                status: 0
            };
        } else {
            // Something happened in setting up the request that triggered an Error
            return {
                message: error.message,
                status: 0
            };
        }
    }
}

export default GenericService; 